import { IconButton } from "@/components/button/IconButton"
import { Combobox } from "@/components/combobox/Combobox"
import { Label } from "@/components/ui/label"
import {
  CreateMessageData,
  messagesApi,
  UpdateMessageData,
} from "@/fetch/api/messages"
import { getDistricts } from "@/fetch/districts"
import { useFetchHook } from "@/lib/hooks/fetchHook"
import { District, Message } from "../../../../types"
import { ArrowUp, PlusIcon, X, XIcon } from "lucide-react"
import { ChangeEvent, useMemo, useRef, useState } from "react"
import { toast } from "react-toastify"
import styled from "styled-components"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import pdfDataPreview from "./pdfIconDataUri"
import { formatAttachmentUrl } from "@/lib/formatAttachmentUrl"

const StyledBox = styled.div`
  border: 1px solid #ecf1f4;
  border-radius: 8px;
  padding: 16px;
  flex-shrink: 0;
  max-width: 600px;
  width: 100%;
  margin: 0 auto;

  &:has(textarea:focus) {
    border-color: #8c5fec;
  }
`

const StyledTextArea = styled.textarea`
  resize: none;
  width: 100%;
  field-sizing: content;

  &:focus {
    outline: none;
  }
`

const BottomRow = styled.div`
  padding-top: 8px;
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  img {
    margin-bottom: 8px;
  }
`

const ImagePreview = styled.div`
  position: relative;
  width: fit-content;
`

const StyledIconButton = styled(IconButton)`
  background: #1f1f1f;
  border-radius: 8px;
  width: 20px;
  height: 20px;
  position: absolute;
  right: 2px;
  top: 2px;
  display: flex;
  justify-content: center;
  align-items: center;
`

const SendButton = styled(IconButton)`
  width: 32px;
  height: 32px;
  border-radius: 32px;
  background: #8c5fec;
  color: #fff;
  display: flex;
  justify-content: center;
  align-items: center;

  &[disabled] {
    opacity: 0.5;
  }

  &:hover {
    background: var(--iris-hover);
  }
`

const NewMessageActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const Attachments = styled.div`
  display: flex;
  gap: 8px;

  img {
    width: 120px;
  }
`

const StyledFileLink = styled.a`
  text-decoration: none;
  gap: 4px;
  color: var(--iris);
  font-size: 12px;
  font-weight: 500;
  &:hover {
    color: var(--iris-hover);
  }
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 120px;
  border: 1px solid var(--accent);
  border-radius: 8px;
  padding: 8px;
  background: #fafcfe;
`

/**
 * [x] overall box style / layout
 * [x] enter text - textarea
 * [x] upload file - file upload
 * [x] preview uploaded file
 * [x] save button
 * [x] select district
 */

type Props = {
  onSuccess: () => void
  cancelEditMessage: () => void
  editMessage: Message | null
}
export const CreateNewNotification = ({
  onSuccess,
  cancelEditMessage,
  editMessage,
}: Props) => {
  const [loading, setLoading] = useState(false)
  const [text, setText] = useState(editMessage?.content ?? "")
  const [file, setFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<null | string>(null)
  // needed for clearing input and being able to select same file
  const [inputKey, setInputKey] = useState(1)
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const [selectedDistricts, setSelectedDistricts] = useState<number[]>(
    editMessage?.districtIds?.split(",").map((x) => Number(x)) ?? []
  )
  const { data: districts } = useFetchHook<District[]>(getDistricts, {
    defaultValue: [],
  })
  const districtOptions = useMemo(() => {
    return [{ name: "Visi iecirkņi", id: undefined }, ...districts]
  }, [districts])

  const showAttachments = preview || (editMessage?.attachments?.length ?? 0) > 0

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file && file.type.startsWith("image/")) {
      setFile(file)
      const reader = new FileReader()
      reader.onloadend = () => setPreview(reader.result as string)
      reader.readAsDataURL(file)
    } else if (file && file.type.startsWith("application/")) {
      setFile(file)
      // set predefined file icon in preview
      setPreview(pdfDataPreview)
    } else {
      setFile(null)
      setPreview(null)
    }
  }

  const handleButtonClick = () => {
    fileInputRef.current?.click()
  }

  const handleClear = () => {
    setPreview(null)
    setInputKey((prev) => prev + 1)
  }

  const handleSend = async () => {
    try {
      setLoading(true)
      if (editMessage) {
        const body: UpdateMessageData = {
          content: text,
        }
        await messagesApi.updateMessage(editMessage.id, body)
        toast("Ziņojums veiksmīgi atjaunots")
        cancelEditMessage()
        onSuccess()
      } else {
        const body: CreateMessageData = {
          content: text,
          toUserIds: [],
          toDistrictIds: selectedDistricts.filter(
            (id) => id !== undefined
          ) as number[],
          attachments: file ? [file] : [],
          systemMessage: true,
        }
        await messagesApi.createMessage(body)
        toast("Ziņojums veiksmīgi izveidots")
        setText("")
        setInputKey((prev) => prev + 1)
        setPreview(null)
        setFile(null)
        setSelectedDistricts([])
        onSuccess()
      }
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }
  return (
    <StyledBox>
      <div style={{ marginBottom: "12px" }}>
        <Label style={{ marginBottom: "4px", display: "block" }}>
          Iecirknis
        </Label>
        <Combobox
          selected={selectedDistricts}
          options={districtOptions}
          onChange={(value) => setSelectedDistricts(value)}
          disabled={!!editMessage}
        />
      </div>
      <StyledTextArea
        placeholder="Ievadiet ziņojumu"
        value={text}
        onChange={(e) => setText(e.target.value)}
      />

      <BottomRow>
        <div>
          {showAttachments && (
            <Attachments>
              {editMessage?.attachments?.map((attach) => {
                switch (attach.mimeType) {
                  case "image/jpeg":
                  case "image/png": {
                    return (
                      <img
                        src={formatAttachmentUrl(attach.url)}
                        alt=""
                        key={attach.id}
                      />
                    )
                  }
                  case "application/pdf":
                  case "application/x-pdf": {
                    return (
                      <StyledFileLink
                        href={formatAttachmentUrl(attach.url)}
                        download={attach.name}
                        target="_blank"
                        rel="noopener noreferrer"
                        key={attach.id}
                        title={attach.name}
                      >
                        <img src={pdfDataPreview} alt={attach.name} />
                        <span>{attach.name}</span>
                      </StyledFileLink>
                    )
                  }
                  default: {
                    return null
                  }
                }
              })}
              {preview && (
                <ImagePreview>
                  <img
                    src={preview}
                    alt="Preview"
                    className="mt-2 h-24 rounded-lg object-cover shadow"
                  />
                  <StyledIconButton onClick={handleClear}>
                    <X color="#fff" width={16} height={16} />
                  </StyledIconButton>
                </ImagePreview>
              )}
            </Attachments>
          )}
          <input
            key={inputKey}
            type="file"
            id="file-upload"
            accept="image/*, application/pdf"
            onChange={handleFileChange}
            ref={fileInputRef}
            style={{ display: "none" }}
            disabled={!!editMessage}
          />
          {!editMessage && (
            <label htmlFor="file-upload">
              <IconButton onClick={handleButtonClick}>
                <PlusIcon color="#606B85" />
              </IconButton>
            </label>
          )}
        </div>
        <NewMessageActions>
          {editMessage && (
            <TooltipProvider delayDuration={100}>
              <Tooltip>
                <TooltipTrigger>
                  <IconButton onClick={() => cancelEditMessage()}>
                    <XIcon width={20} height={20} />
                  </IconButton>
                </TooltipTrigger>
                <TooltipContent>Atcelt</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          <SendButton onClick={handleSend} disabled={loading}>
            <ArrowUp width={20} height={20} />
          </SendButton>
        </NewMessageActions>
      </BottomRow>
    </StyledBox>
  )
}
